import { RobotRead } from 'api/schema'
import MapboxCommon from 'components/MapboxMap/MapboxCommon'
import { IOverlayItem } from 'components/MapboxMap/OverlayControl/OverlayControl'
import RobotOnMap from 'components/RobotOnMap/RobotOnMap'
import { Feature, GeoJsonProperties, Geometry } from 'geojson'
import React, { useImperativeHandle } from 'react'
import { useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { MissionActions } from 'store/actions/mission'
import {
  selectGeoTiffLayer,
  selectMission,
  selectShowCleaningZones,
  selectShowRestrictZones,
} from 'store/selectors/mission'
import store from 'store/store'
import { missionThunks } from 'store/thunks/mission'
import { MISSION_MAP_DRAWING_MODES, MISSION_ZONE_TYPES, TRANSFER_CALCULATING_STATUSES } from 'utils/constants'
import { ITrajectory } from 'utils/interfaces'
import { IMapboxElement } from 'utils/interfaces'
import { zoomMapTo } from 'utils/mapUtils'

import DrawControl from './DrawControl/DrawControl'
import drawStyles from './DrawControl/drawStyles'
import CleaningPointsOnMap from './Layers/CleaningPointsOnMap'
import CleaningRoutesOnMap from './Layers/CleaningRoutesOnMap'
import CleaningZonesNamesOnMap from './Layers/CleaningZonesNamesOnMap'
import CleaningZonesOnMap from './Layers/CleaningZonesOnMap'
import GeoTiffLayer from './Layers/GeoTiffLayer'
import MissionStartFinishPoints from './Layers/MissionStartFinishPoints'
import RestrictZonesOnMap from './Layers/RestrictZonesOnMap'
import TransferRoutesOnMap from './Layers/TransferRoutesOnMap'

let globalLastZoomedLayerId: string | number | undefined = undefined

interface MissionMapProps {
  children?: React.ReactNode
  selectedRobot?: RobotRead
  onSelectRobotClick?: () => void
}

const MissionMap = React.forwardRef<IMapboxElement, MissionMapProps>((props, ref) => {
  const dispatch = useDispatch()
  const mapRef = useRef<IMapboxElement>(null)
  const mission = useSelector(selectMission)
  const showCleaningZones = useSelector(selectShowCleaningZones)
  const showRestrictZones = useSelector(selectShowRestrictZones)
  const geoTiffLayer = useSelector(selectGeoTiffLayer)

  const onFeatureCreated = (evt: { features: Feature[] }) => {
    store.dispatch(missionThunks.createFeature(evt))
  }

  const onFeatureGeometryEdited = (evt: { features: Feature[]; action: string }) => {
    store.dispatch(missionThunks.editFeature(evt))
  }

  useImperativeHandle(ref, () => ({
    getMap() {
      return mapRef?.current?.getMap()
    },
  }))

  // Track which zone/route we've already zoomed to, so we don't re-zoom on every geometry edit
  const zoomedRouteIdRef = useRef<number | string | null>(null)
  const zoomedZoneIdRef = useRef<number | string | null>(null)

  useEffect(() => {
    const map = mapRef.current?.getMap()
    if (!map) return

    let featuresToZoom: Feature[] = []

    if (mission.highlightedFeature) {
      featuresToZoom.push(mission.highlightedFeature)
    }
    // Only zoom to tempZone when it's first set (different zone), not on every geometry change
    if (mission.tempZone) {
      const zoneId = mission.tempZone.properties?.zone_id ?? mission.tempZone.properties?.id_on_map
      if (zoomedZoneIdRef.current !== zoneId) {
        zoomedZoneIdRef.current = zoneId
        featuresToZoom.push(mission.tempZone)
      }
    } else {
      zoomedZoneIdRef.current = null
    }
    // Only zoom to tempZoneRoute when it's first set (different route), not on every geometry change
    if (mission.tempZoneRoute) {
      const routeId = mission.tempZoneRoute.properties?.zone_id ?? mission.tempZoneRoute.properties?.id_on_map
      if (zoomedRouteIdRef.current !== routeId) {
        zoomedRouteIdRef.current = routeId
        featuresToZoom.push(mission.tempZoneRoute)
      }
    } else {
      // Reset when route is cleared
      zoomedRouteIdRef.current = null
    }

    if (featuresToZoom.length > 0) {
      zoomMapTo(map, featuresToZoom)
    }
  }, [mission.highlightedFeature, mission.tempZone, mission.tempZoneRoute])

  useEffect(() => {
    const map = mapRef.current?.getMap()
    const layer = mission.geoTiffLayer?.layer

    // only zoom if layer exists, has bbox, and is different from last zoomed layer
    if (!map || !layer?.bbox || globalLastZoomedLayerId === layer.id) return

    const featuresToZoom: Feature[] = [
      {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: layer.bbox.coordinates,
        },
        properties: {},
      },
    ]

    zoomMapTo(map, featuresToZoom)
    globalLastZoomedLayerId = layer.id
  }, [mission.geoTiffLayer?.layer?.bbox, mission.geoTiffLayer?.layer?.id])

  const transferRoutes = useMemo(() => {
    let routes: Array<ITrajectory> = []

    // Combine routes and prioritize userGeometry for display
    const allRoutes = [...mission.missionPoints.routes, ...mission.transferRoutes].map(route => {
      // If route is new (not calculated), show userGeometry (straight line)
      if (
        route.properties.status === TRANSFER_CALCULATING_STATUSES.NEW &&
        route.userGeometry &&
        route.userGeometry.coordinates &&
        route.userGeometry.coordinates.length > 0
      ) {
        return {
          ...route,
          geometry: route.userGeometry,
        }
      }
      // Otherwise (status 'ok', 'error', etc.), show standard geometry (calculated path)
      return route
    })

    routes.push(...allRoutes)
    routes = routes.filter(route => route.id !== mission.editingTransfer?.id)
    return routes
  }, [mission.transferRoutes, mission.missionPoints.routes, mission.editingTransfer?.id])

  const initMap = () => {
    const map = mapRef.current?.getMap()

    const geoTiffLayerFeatures: Feature<Geometry, GeoJsonProperties>[] = mission.geoTiffLayer?.layer?.bbox
      ? [
          {
            type: 'Feature' as const,
            geometry: {
              type: 'Polygon',
              coordinates: mission.geoTiffLayer.layer.bbox.coordinates,
            },
            properties: {},
          },
        ]
      : []

    const features: Feature<Geometry, GeoJsonProperties>[] = [
      ...mission.cleaningZones,
      ...mission.restrictZones,
      ...mission.cleaningRoutes,
      ...transferRoutes,
    ]

    // Only add bbox if we haven't zoomed to this layer yet, OR if there's no other content
    if (globalLastZoomedLayerId !== mission.geoTiffLayer?.layer?.id || features.length === 0) {
      features.push(...geoTiffLayerFeatures)
    }

    zoomMapTo(map, features)

    const setHighlightedFeature = (id: string | undefined) => {
      dispatch(MissionActions.setHighlightedFeatureInList(id))
    }
    map?.on('click', () => setHighlightedFeature(undefined))
    map?.on('click', 'gl-draw-polygon-fill-cleaning-zone', e =>
      setHighlightedFeature(e?.features && e?.features[0].properties?.id_on_map)
    )
    map?.on('click', 'restrict-zones-fill', e =>
      setHighlightedFeature(e?.features && e?.features[0].properties?.id_on_map)
    )
    map?.on('click', 'transfer-route-halo-style', e =>
      setHighlightedFeature(e?.features && e?.features[0].properties?.id_on_map)
    )
    map?.on('click', 'gl-draw-cleaning-route-launch-point', e =>
      setHighlightedFeature(e?.features && e?.features[0].properties?.id_on_map)
    )
    map?.on('click', 'gl-draw-cleaning-route-finish-point1', e =>
      setHighlightedFeature(e?.features && e?.features[0].properties?.id_on_map)
    )
    map?.on('click', 'gl-draw-cleaning-routes', e =>
      setHighlightedFeature(e?.features && e?.features[0].properties?.id_on_map)
    )
    map?.on('click', 'mission-finish-point1', e =>
      setHighlightedFeature(e?.features && e?.features[0].properties?.id_on_map)
    )
    map?.on('click', 'mission-start-point', e =>
      setHighlightedFeature(e?.features && e?.features[0].properties?.id_on_map)
    )
    map?.on('click', 'text-layer', e => setHighlightedFeature(e?.features && e?.features[0].properties?.id_on_map))
    map?.on('click', 'recorded-route-text-layer', e =>
      setHighlightedFeature(e?.features && e?.features[0].properties?.id_on_map)
    )

    const interactiveLayers = [
      'gl-draw-polygon-fill-cleaning-zone',
      'restrict-zones-fill',
      'transfer-route-halo-style',
      'gl-draw-cleaning-route-launch-point',
      'gl-draw-cleaning-route-finish-point1',
      'gl-draw-cleaning-routes',
      'mission-finish-point1',
      'mission-start-point',
      'text-layer',
      'recorded-route-text-layer',
    ]

    interactiveLayers.forEach(layer => {
      map?.on('mouseenter', layer, () => (map.getCanvas().style.cursor = 'pointer'))
      map?.on('mouseleave', layer, () => (map.getCanvas().style.cursor = ''))
    })
  }

  const onMapMoved = () => {
    dispatch(MissionActions.setHighlightedFeature(undefined))
  }

  const launchPoints = useMemo(() => {
    return mission.launchPoints.filter(launchPoint => launchPoint !== mission.editingLaunchPoint)
  }, [mission.editingLaunchPoint, mission.launchPoints])

  const restrictZones = useMemo(() => {
    return mission.restrictZones.filter(restrictZone => restrictZone.id !== mission.editingZone?.id)
  }, [mission.editingZone, mission.restrictZones])

  const cleaningZones = useMemo(() => {
    return mission.cleaningZones.filter(cleaningZone => cleaningZone.id !== mission.editingZone?.id)
  }, [mission.editingZone, mission.cleaningZones])

  // const cleaningRoutes = useMemo(() => {
  //   return mission.cleaningRoutes.filter(route => route.properties.zone_id !== mission.editingZoneRoute?.properties.zone_id)
  // }, [mission.editingZoneRoute, mission.cleaningRoutes])

  const startPoint = useMemo(() => {
    if (mission.missionPoints.startPoint !== mission.missionPoints.editingPoint) {
      return mission.missionPoints.startPoint
    }
  }, [mission.missionPoints.editingPoint, mission.missionPoints.startPoint])

  const finishPoint = useMemo(() => {
    if (mission.missionPoints.finishPoint !== mission.missionPoints.editingPoint) {
      return mission.missionPoints.finishPoint
    }
  }, [mission.missionPoints.editingPoint, mission.missionPoints.finishPoint])

  const switchShowGeoTiffLayer = () => {
    if (geoTiffLayer.layer) {
      dispatch(MissionActions.setShowGeoTiffLayer(!geoTiffLayer.show))
    }
  }

  const overlayItems: IOverlayItem[] = [
    {
      onClick: switchShowGeoTiffLayer,
      active: geoTiffLayer.show,
      disabled: !geoTiffLayer.layer?.urlTiles,
      content: <>Geotiff layer</>,
    },
    {
      onClick: () => dispatch(MissionActions.setShowCleaningZones(!showCleaningZones)),
      active: showCleaningZones,
      disabled: false,
      content: <>Cleaning Zones</>,
    },
    {
      onClick: () => dispatch(MissionActions.setShowRestrictZones(!showRestrictZones)),
      active: showRestrictZones,
      disabled: false,
      content: <>Restrict Zones</>,
    },
    {
      onClick: () => props.onSelectRobotClick && props.onSelectRobotClick(),
      active: !!props.selectedRobot,
      disabled: false,
      content: <>{props.selectedRobot ? `Clear: ${props.selectedRobot.name}` : 'Select Robot'}</>,
    },
  ]

  return (
    <MapboxCommon ref={mapRef} onMapMoved={onMapMoved} onMapInited={initMap} overlayItems={overlayItems} maxZoom={23}>
      <GeoTiffLayer show={geoTiffLayer.show} />
      <RestrictZonesOnMap restrictZones={showRestrictZones ? restrictZones : []} />
      <CleaningZonesOnMap cleaningZones={showCleaningZones ? cleaningZones : []} />
      <CleaningRoutesOnMap cleaningRoutes={showCleaningZones ? mission.cleaningRoutes : []} />
      {/* <CleaningRoutesOnMap cleaningRoutes={showCleaningZones ? cleaningRoutes : []} /> */}
      <DrawControl
        userProperties={true}
        displayControlsDefault={false}
        onCreate={onFeatureCreated}
        onUpdate={onFeatureGeometryEdited}
        onDelete={() => {}}
        mode={mission.mapMode}
        tempZone={mission.tempZone}
        tempZoneRoute={mission.tempZoneRoute}
        tempLaunchPoint={mission.tempLaunchPoint}
        tempMissionPoint={mission.missionPoints.tempPoint}
        styles={drawStyles}
        tempTransfer={mission.tempTransfer}
        setEditingTempLaunchPoint={() =>
          dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE_LAUNCH_POINT))
        }
        setEditingTempZone={() => dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE))}
        setEditingTempZoneRoute={() =>
          dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE_ROUTE))
        }
      />
      <TransferRoutesOnMap transferRoutes={showCleaningZones ? transferRoutes : []} />
      <CleaningPointsOnMap
        cleaningRoutes={showCleaningZones ? mission.cleaningRoutes : []}
        launchPoints={showCleaningZones ? launchPoints : []}
      />
      <MissionStartFinishPoints startPoint={startPoint} finishPoint={finishPoint} />
      <CleaningZonesNamesOnMap cleaningZones={showCleaningZones ? cleaningZones : []} />
      {props.children}
      {props.selectedRobot && <RobotOnMap map={mapRef.current?.getMap()} />}
    </MapboxCommon>
  )
})

export default MissionMap
